import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { api } from '@/lib/api';
import { SuccessPanel } from './shared';

/* ─── Schedule Meeting Modal ─── */

interface ScheduleMeetingModalProps {
    open: boolean;
    caseId: number;
    onClose: () => void;
    onCreated?: () => void;
}

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

function getMonthDays(year: number, month: number) {
    // Returns array of { day, isCurrentMonth } for a full calendar grid, Monday-first
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const daysInPrev = new Date(year, month, 0).getDate();

    // Mon=0 .. Sun=6
    const startDow = (firstDay.getDay() + 6) % 7;

    const cells: { day: number; isCurrent: boolean; date: Date }[] = [];

    // Leading days from previous month
    for (let i = startDow - 1; i >= 0; i--) {
        cells.push({
            day: daysInPrev - i,
            isCurrent: false,
            date: new Date(year, month - 1, daysInPrev - i),
        });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ day: d, isCurrent: true, date: new Date(year, month, d) });
    }
    // Trailing days to fill 6 rows (42 cells)
    let nextDay = 1;
    while (cells.length < 42) {
        cells.push({ day: nextDay, isCurrent: false, date: new Date(year, month + 1, nextDay) });
        nextDay++;
    }
    return cells;
}

function isSameDate(a: Date | null, b: Date) {
    if (!a) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toDateString(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function ScheduleMeetingModal({ open, caseId, onClose, onCreated }: ScheduleMeetingModalProps) {
    const [viewMonth, setViewMonth] = useState(() => new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [hour, setHour] = useState('12');
    const [minute, setMinute] = useState('00');
    const [period, setPeriod] = useState<'AM' | 'PM'>('PM');
    const [title, setTitle] = useState('Meeting');
    const [isLoading, setIsLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setViewMonth(new Date());
            setSelectedDate(null);
            setHour('12');
            setMinute('00');
            setPeriod('PM');
            setTitle('Meeting');
            setDone(false);
            setError('');
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const cells = getMonthDays(viewMonth.getFullYear(), viewMonth.getMonth());

    const handleSubmit = async () => {
        if (!selectedDate) {
            setError('Please select a date.');
            return;
        }
        const h = parseInt(hour, 10);
        const m = parseInt(minute, 10);
        if (Number.isNaN(h) || Number.isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59) {
            setError('Please enter a valid time.');
            return;
        }

        // Convert 12h to 24h
        let hour24 = h % 12;
        if (period === 'PM') hour24 += 12;
        const scheduledTime = `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

        setIsLoading(true);
        setError('');
        try {
            await api.post(`/api/cases/${caseId}/schedules`, {
                title: title.trim() || 'Meeting',
                scheduledDate: toDateString(selectedDate),
                scheduledTime,
            });
            setDone(true);
            onCreated?.();
        } catch (err) {
            const msg =
                err instanceof Error && err.message ? err.message : 'Failed to create schedule. Please try again.';
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-60 bg-black/40" onClick={onClose} />
            <div className="fixed inset-0 z-61 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                    <div className="flex items-center gap-4 px-6 pt-6 pb-4">
                        <button
                            type="button"
                            title="Close"
                            onClick={onClose}
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f5f5f5] text-[#555] transition-colors hover:bg-[#eaeaea]"
                        >
                            <MdClose size={18} />
                        </button>
                        <h2 className="text-lg font-bold text-secondary">Schedule Meeting</h2>
                    </div>

                    {done ? (
                        <SuccessPanel
                            title="Meeting Schedule Successfully!"
                            message="The meeting has been added to the case schedule. Participants will be notified."
                            gradientId="schedule-grad"
                            onClose={onClose}
                        />
                    ) : (
                        <>
                            <p className="px-6 pb-4 text-[12px] leading-relaxed text-[#888]">
                                Next, you'll need to choose a date and time for your appointment. Simply pick a time
                                that works for you from the available slots in your selected lawyer's calendar to
                                confirm your booking.
                            </p>

                            {/* Calendar */}
                            <div className="px-6">
                                <p className="mb-3 text-[13px] font-bold text-secondary">Select Date</p>
                                <div className="mb-3 flex items-center justify-center gap-6">
                                    <button
                                        type="button"
                                        title="Previous month"
                                        onClick={() =>
                                            setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
                                        }
                                        className="text-[#999] hover:text-secondary"
                                    >
                                        ‹
                                    </button>
                                    <span className="text-[13px] font-bold text-secondary">
                                        {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                                    </span>
                                    <button
                                        type="button"
                                        title="Next month"
                                        onClick={() =>
                                            setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
                                        }
                                        className="text-[#999] hover:text-secondary"
                                    >
                                        ›
                                    </button>
                                </div>

                                <div className="grid grid-cols-7 gap-1 text-center">
                                    {WEEKDAYS.map((wd, i) => (
                                        <div key={i} className="py-1 text-[10px] font-medium text-primary">
                                            {wd}
                                        </div>
                                    ))}
                                    {cells.map((cell, i) => {
                                        const isSelected = isSameDate(selectedDate, cell.date);
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => {
                                                    if (cell.isCurrent) setSelectedDate(cell.date);
                                                }}
                                                disabled={!cell.isCurrent}
                                                className={`flex h-8 w-8 items-center justify-center rounded-full text-[12px] transition-colors ${
                                                    isSelected
                                                        ? 'bg-primary font-bold text-white'
                                                        : cell.isCurrent
                                                          ? 'font-semibold text-secondary hover:bg-[#f5f5f5]'
                                                          : 'text-[#ccc]'
                                                }`}
                                            >
                                                {cell.day}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Time */}
                            <div className="mt-6 px-6">
                                <p className="mb-3 text-[13px] font-bold text-secondary">Select Time</p>
                                <div className="flex items-center gap-3 rounded-lg border border-[#D2D2D2] bg-white px-4 py-3">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={hour}
                                        onChange={(e) => setHour(e.target.value.replace(/\D/g, '').slice(0, 2))}
                                        placeholder="12"
                                        title="Hour"
                                        className="w-10 bg-transparent text-center text-[13px] font-semibold text-secondary outline-none"
                                    />
                                    <span className="text-[13px] font-semibold text-secondary">:</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={minute}
                                        onChange={(e) => setMinute(e.target.value.replace(/\D/g, '').slice(0, 2))}
                                        placeholder="00"
                                        title="Minute"
                                        className="w-10 bg-transparent text-center text-[13px] font-semibold text-secondary outline-none"
                                    />
                                    <div className="ml-auto flex gap-1">
                                        {(['AM', 'PM'] as const).map((p) => (
                                            <button
                                                key={p}
                                                type="button"
                                                onClick={() => setPeriod(p)}
                                                className={`rounded px-3 py-1 text-[12px] font-semibold transition-colors ${
                                                    period === p
                                                        ? 'bg-secondary text-white'
                                                        : 'text-[#555] hover:bg-[#f5f5f5]'
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="mt-4 px-6">
                                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-600">
                                        {error}
                                    </div>
                                </div>
                            )}

                            <div className="px-6 pb-6 pt-6">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={isLoading || !selectedDate}
                                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-60"
                                >
                                    {isLoading ? 'Scheduling…' : 'Confirm Meeting'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
}
